import { useState, useMemo } from 'react';
import { MarkdownRenderer } from '../common/MarkdownRenderer';
import { ConfidenceBadge } from '../common/ConfidenceBadge';
import { FeedbackButtons } from '../feedback/FeedbackButtons';
import { Database, Code, ChevronDown, ChevronRight, Lightbulb, BarChart3, Table2, Braces } from 'lucide-react';
import type { Message as MessageType } from '../../types';
import { useChatStore } from '../../store/chatStore';
import { useDataProfile } from './useDataProfile';
import { selectChartType } from './chartSelector';
import { parseMarkdownTable } from './parseMarkdownTable';
import { parseEchartsJson } from './parseEchartsJson';
import { EraxChart } from './charts/EraxChart';
import { EChartsView } from './charts/EChartsView';
import { DataTableView } from './charts/DataTableView';

type DataTab = 'chart' | 'table' | 'raw';

/** Personas that are allowed to see the ERAX SQL query (developer, support). Hidden for client. */
const PERSONAS_WITH_SQL = new Set(['developer', 'support']);

/** Remove SQL disclosure block from ERAX response content when persona must not see SQL. */
function stripSqlDisclosureFromContent(content: string): string {
  if (!content) return content;
  // ERAX can embed <details><summary>View SQL Query</summary>...SQL...</details> in the response
  const re = /<details[^>]*>[\s\S]*?<\/details>/gi;
  return content.replace(re, (block) => {
    if (/view\s*sql\s*query|sql\s*query/i.test(block)) return '';
    return block;
  }).replace(/\n{3,}/g, '\n\n').trim();
}

interface EraxResponseProps {
  message: MessageType;
  onSuggestedQuestionClick?: (question: string) => void;
}

/**
 * Renders an ERAX (Text-to-SQL) data query response with:
 * - Module badge showing the data source
 * - Markdown-rendered response (tables, narrative)
 * - Auto-detected chart with Chart / Table / Raw toggle
 * - Collapsible SQL query disclosure
 * - Suggested follow-up questions
 */
export function EraxResponse({ message, onSuggestedQuestionClick }: EraxResponseProps) {
  const [sqlExpanded, setSqlExpanded] = useState(false);
  const { setSuggestedInput, persona } = useChatStore();
  const erax = message.eraxMeta;
  const showSqlQuery = PERSONAS_WITH_SQL.has(persona);

  // ── ECharts JSON detection ───────────────────────────────────────
  // ERAX may embed pre-built ECharts configs via :::ECHARTS_JSON_START::: delimiters.
  // When present, use those directly instead of the markdown-table → profile → chart pipeline.
  const echartsResult = useMemo(
    () => parseEchartsJson(message.content),
    [message.content],
  );
  const hasEchartsJson = echartsResult !== null && echartsResult.charts.length > 0;

  // Use cleaned content (ECharts blocks stripped) when present; otherwise use original content
  const baseContent = hasEchartsJson ? echartsResult!.cleanContent : message.content;
  const contentToRender = showSqlQuery ? baseContent : stripSqlDisclosureFromContent(baseContent);

  // ── Legacy data pipeline (fallback when no ECharts JSON) ─────────
  const dataArray = useMemo(() => {
    if (hasEchartsJson) return null; // Skip legacy pipeline when we have ECharts JSON
    if (erax?.data && Array.isArray(erax.data) && erax.data.length > 0) {
      return erax.data as Record<string, unknown>[];
    }
    if (erax && message.content) {
      const parsed = parseMarkdownTable(message.content);
      if (parsed && parsed.length > 0) return parsed;
    }
    return null;
  }, [hasEchartsJson, erax?.data, erax, message.content]);

  const profile = useDataProfile(dataArray);
  const recommendation = useMemo(
    () => (profile ? selectChartType(profile) : null),
    [profile],
  );

  // Default tab: chart if we have ECharts JSON or a non-table recommendation, otherwise table
  const defaultTab: DataTab =
    hasEchartsJson || (recommendation && recommendation.chartType !== 'table') ? 'chart' : 'table';
  const [activeTab, setActiveTab] = useState<DataTab>(defaultTab);

  const formatTime = (date: Date) => {
    return new Date(date).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleSuggestedClick = (question: string) => {
    if (onSuggestedQuestionClick) {
      onSuggestedQuestionClick(question);
    } else {
      setSuggestedInput(question);
    }
  };

  const hasLegacyData = dataArray && dataArray.length > 0;

  return (
    <div className="flex gap-2.5 animate-fade-in" role="article" aria-label="Data query response">
      {/* Avatar */}
      <div className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center bg-blue-100">
        <Database size={13} className="text-blue-600" />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        {/* Header */}
        <div className="flex items-center gap-1.5 mb-0.5">
          <span className="text-xs font-medium text-gray-700">Data Assistant</span>
          <span className="text-[10px] text-gray-400">{formatTime(message.timestamp)}</span>
          {message.confidenceLevel && (
            <ConfidenceBadge level={message.confidenceLevel} score={message.confidence} />
          )}
        </div>

        {/* Module badge */}
        {erax?.module && (
          <div className="mb-1">
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-blue-50 text-blue-700 font-medium">
              <Database size={9} />
              {erax.module}
            </span>
          </div>
        )}

        {/* Message bubble */}
        <div className="inline-block max-w-full rounded-2xl px-3 py-2 text-[13px] leading-snug bg-blue-50 text-gray-800 rounded-tl-md">
          <div className="markdown-content">
            <MarkdownRenderer content={contentToRender} />
          </div>
        </div>

        {/* ── ECharts JSON Visualization (new JSON format from ERAX) ── */}
        {hasEchartsJson && (
          <div className="mt-2 flex flex-col items-center w-full">
            <div className="w-full max-w-2xl">
              {/* Tab toggle */}
              <div className="flex items-center gap-0.5 mb-1">
                <TabButton
                  active={activeTab === 'chart'}
                  onClick={() => setActiveTab('chart')}
                  icon={<BarChart3 size={10} />}
                  label="Chart"
                />
                <TabButton
                  active={activeTab === 'raw'}
                  onClick={() => setActiveTab('raw')}
                  icon={<Braces size={10} />}
                  label="Raw"
                />
              </div>

              {/* Tab content */}
              {activeTab === 'chart' && echartsResult!.charts.map((chartOption, idx) => (
                <div key={idx} className="mt-1 rounded-lg bg-white border border-gray-200 p-1 overflow-hidden">
                  <EChartsView option={chartOption} />
                </div>
              ))}
              {activeTab === 'raw' && (
                <div className="rounded-lg bg-gray-900 p-2 overflow-x-auto max-h-60">
                  <pre className="text-[10px] text-gray-300 font-mono whitespace-pre-wrap">
                    {JSON.stringify(echartsResult!.charts, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Legacy Data Visualization Section (fallback: md table → profile → chart) ── */}
        {!hasEchartsJson && hasLegacyData && recommendation && (
          <div className="mt-2 flex flex-col items-center w-full">
            <div className="w-full max-w-2xl">
            {/* Tab toggle */}
            <div className="flex items-center gap-0.5 mb-1">
              <TabButton
                active={activeTab === 'chart'}
                onClick={() => setActiveTab('chart')}
                icon={<BarChart3 size={10} />}
                label="Chart"
                disabled={recommendation.chartType === 'table'}
              />
              <TabButton
                active={activeTab === 'table'}
                onClick={() => setActiveTab('table')}
                icon={<Table2 size={10} />}
                label="Table"
              />
              <TabButton
                active={activeTab === 'raw'}
                onClick={() => setActiveTab('raw')}
                icon={<Braces size={10} />}
                label="Raw"
              />
            </div>

            {/* Tab content */}
            {activeTab === 'chart' && recommendation.chartType !== 'table' && (
              <EraxChart data={dataArray!} recommendation={recommendation} />
            )}
            {(activeTab === 'table' || (activeTab === 'chart' && recommendation.chartType === 'table')) && (
              <DataTableView data={dataArray!} />
            )}
            {activeTab === 'raw' && (
              <div className="rounded-lg bg-gray-900 p-2 overflow-x-auto max-h-60">
                <pre className="text-[10px] text-gray-300 font-mono whitespace-pre-wrap">
                  {JSON.stringify(dataArray, null, 2)}
                </pre>
              </div>
            )}
            </div>
          </div>
        )}

        {/* SQL Query — collapsible */}
        {erax?.sqlQuery && (
          <div className="mt-1">
            <button
              onClick={() => setSqlExpanded(!sqlExpanded)}
              className="flex items-center gap-1 text-[10px] font-medium text-gray-500 uppercase tracking-wide hover:text-gray-700 transition-colors"
            >
              {sqlExpanded ? <ChevronDown size={10} /> : <ChevronRight size={10} />}
              <Code size={9} />
              View SQL Query
            </button>
            {sqlExpanded && (
              <div className="mt-1 rounded-lg bg-gray-900 p-2 overflow-x-auto">
                <pre className="text-[11px] text-green-300 font-mono whitespace-pre-wrap">
                  {erax.sqlQuery}
                </pre>
              </div>
            )}
          </div>
        )}

        {/* Query type badge */}
        <div className="mt-1">
          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] bg-blue-100 text-blue-700 capitalize">
            data query
          </span>
        </div>

        {/* Suggested follow-up questions */}
        {erax?.suggestedQuestions && erax.suggestedQuestions.length > 0 && (
          <div className="mt-2">
            <div className="flex items-center gap-1 mb-1">
              <Lightbulb size={10} className="text-amber-500" />
              <span className="text-[10px] font-medium text-gray-500 uppercase tracking-wide">
                Suggested follow-ups
              </span>
            </div>
            <div className="flex flex-wrap gap-1">
              {erax.suggestedQuestions.map((question, idx) => (
                <button
                  key={idx}
                  onClick={() => handleSuggestedClick(question)}
                  className="text-[11px] px-2 py-1 rounded-full bg-gray-100 text-gray-700 hover:bg-blue-100 hover:text-blue-700 transition-colors text-left"
                >
                  {question}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Feedback buttons */}
        {!message.isLoading && (
          <FeedbackButtons messageId={message.id} currentFeedback={message.feedbackGiven} />
        )}
      </div>
    </div>
  );
}

/** Small toggle button for Chart / Table / Raw tabs. */
function TabButton({
  active,
  onClick,
  icon,
  label,
  disabled,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  disabled?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center gap-0.5 px-2 py-1 rounded text-[10px] font-medium transition-colors ${
        active
          ? 'bg-indigo-100 text-indigo-700'
          : disabled
          ? 'text-gray-300 cursor-not-allowed'
          : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
      }`}
    >
      {icon}
      {label}
    </button>
  );
}
